import React, {
  ReactNode,
  useState,
  useContext,
  createContext,
  useCallback
} from 'react';
import { IItinerary2 } from '../../Models/itineraries/itinerary.model';
import { ITrip } from '../../Models/trips/trip.model';
import { ITripActivity } from '../../Models/trips/trip-activity.model';

export interface TripDataContextType {
  // Core trip data
  trip: ITrip | null;
  setTrip: (trip: ITrip) => void;
  updateTrip: (updates: Partial<ITrip>) => void;

  // Refresh control
  refreshTrip: boolean;
  setRefreshTrip: (refresh: boolean) => void;

  // Itineraries
  selectedItinerary: IItinerary2 | null;
  setSelectedItinerary: (itinerary: IItinerary2 | null) => void;
  itinerary: IItinerary2 | null;
  setItinerary: (itinerary: IItinerary2) => void;

  // Operations
  addActivity: (activity: ITripActivity) => void;
  moveActivityBetweenDaysOptimistic: (
    itineraryId: string,
    activityId: string,
    sourceDayNumber: number,
    targetDayNumber: number
  ) => void;
  removeActivityOptimistic: (activityId: string) => ITrip;
}

const TripDataContext = createContext<TripDataContextType | undefined>(
  undefined
);

export const TripDataProvider: React.FC<{ children: ReactNode }> = ({
  children
}) => {
  const [trip, setTripState] = useState<ITrip>({
    id: '',
    title: '',
    imageUrl: '',
    description: '',
    permissions: [],
    isPublic: false,
    createdByUsername: '',
    deleted: false,
    itineraries: [],
    destinations: [],
    destinationCount: 0,
    totalActivities: 0,
    activities: [],
    folders: [],
    itineraryCount: 0,
    photos: [],
    photoCount: 0,
    reels: [],
    reelCount: 0,
    photosWithLocation: 0,
    photosWithCaption: 0,
    isValid: false,
    isReadyForPlanning: false,
    startDate: new Date(),
    endDate: new Date(),
    createdAt: new Date(),
    updatedAt: new Date(),
    isReadyForPublication: false,
    canEdit: false,
    canAccess: false,
    tags: [],
    autoOrganize: false
  });

  const [refreshTrip, setRefreshTrip] = useState<boolean>(false);
  const [itinerary, setItinerary] = useState<IItinerary2>({
    id: '',
    title: '',
    description: '',
    activities: [],
    days: [],
    isPublic: false,
    tripId: '',
    createdBy: '',
    deleted: false
  });
  const [selectedItinerary, setSelectedItinerary] =
    useState<IItinerary2 | null>(null);

  // Helper function to add an activity optimistically
  const addActivity = useCallback((activity: ITripActivity) => {
    console.log('[TripDataContext] Adding activity optimistically:', activity);
    setTripState((prevTrip) => ({
      ...prevTrip,
      activities: [...prevTrip.activities, activity]
    }));
  }, []);

  // Helper function to directly update trip state (for optimistic updates)
  const updateTrip = useCallback((updates: Partial<ITrip>) => {
    console.log('[TripDataContext] Optimistically updating trip:', updates);
    setTripState((prevTrip) => ({
      ...prevTrip,
      ...updates
    }));
  }, []);

  // Optimistically move an activity between days in an itinerary
  const moveActivityBetweenDaysOptimistic = useCallback(
    (
      itineraryId: string,
      activityId: string,
      sourceDayNumber: number,
      targetDayNumber: number
    ) => {
      console.log('[TripDataContext] Optimistically moving activity:', {
        itineraryId,
        activityId,
        sourceDayNumber,
        targetDayNumber
      });

      setTripState((prevTrip) => {
        if (!prevTrip.itineraries) return prevTrip;

        const updatedItineraries = prevTrip.itineraries.map((itin) => {
          if (itin.id !== itineraryId) return itin;

          // Find source and target days
          let sourceDay = itin.days?.find(
            (d) => d.dayNumber === sourceDayNumber
          );
          let targetDay = itin.days?.find(
            (d) => d.dayNumber === targetDayNumber
          );

          if (!sourceDay) {
            console.warn(
              '[TripDataContext] Source day not found:',
              sourceDayNumber
            );
            return itin;
          }

          // Find the activity in the source day
          const activityIndex = sourceDay.activities?.findIndex(
            (a) => a.activityId === activityId
          );

          if (activityIndex === undefined || activityIndex === -1) {
            console.warn(
              '[TripDataContext] Activity not found in source day:',
              activityId
            );
            return itin;
          }

          const activityToMove = sourceDay.activities![activityIndex];

          // Create new days array
          let newDays = itin.days ? [...itin.days] : [];

          // If target day doesn't exist, create missing days
          if (!targetDay) {
            const maxDayNumber = Math.max(
              ...newDays.map((d) => d.dayNumber || 0),
              0
            );

            // Create missing days from maxDayNumber + 1 to targetDayNumber
            for (
              let dayNum = maxDayNumber + 1;
              dayNum <= targetDayNumber;
              dayNum++
            ) {
              newDays.push({
                dayNumber: dayNum,
                title: `Day ${dayNum}`,
                activities: [],
                deleted: false
              } as any);
            }

            // Now target day exists
            targetDay = newDays.find((d) => d.dayNumber === targetDayNumber);
          }

          // Update the days array
          newDays = newDays.map((day) => {
            if (day.dayNumber === sourceDayNumber) {
              // Remove activity from source day
              return {
                ...day,
                activities:
                  day.activities?.filter((a) => a.activityId !== activityId) ||
                  []
              };
            } else if (day.dayNumber === targetDayNumber) {
              // Add activity to target day
              return {
                ...day,
                activities: [...(day.activities || []), activityToMove]
              };
            }
            return day;
          });

          return {
            ...itin,
            days: newDays
          };
        });

        return {
          ...prevTrip,
          itineraries: updatedItineraries
        };
      });

      // Also update selectedItinerary if it's the one being modified
      setSelectedItinerary((prevSelected) => {
        if (!prevSelected || prevSelected.id !== itineraryId)
          return prevSelected;

        let sourceDay = prevSelected.days?.find(
          (d) => d.dayNumber === sourceDayNumber
        );
        let targetDay = prevSelected.days?.find(
          (d) => d.dayNumber === targetDayNumber
        );

        if (!sourceDay) return prevSelected;

        const activityIndex = sourceDay.activities?.findIndex(
          (a) => a.activityId === activityId
        );

        if (activityIndex === undefined || activityIndex === -1)
          return prevSelected;

        const activityToMove = sourceDay.activities![activityIndex];
        let newDays = prevSelected.days ? [...prevSelected.days] : [];

        if (!targetDay) {
          const maxDayNumber = Math.max(
            ...newDays.map((d) => d.dayNumber || 0),
            0
          );

          for (
            let dayNum = maxDayNumber + 1;
            dayNum <= targetDayNumber;
            dayNum++
          ) {
            newDays.push({
              dayNumber: dayNum,
              title: `Day ${dayNum}`,
              activities: [],
              deleted: false
            } as any);
          }
        }

        newDays = newDays.map((day) => {
          if (day.dayNumber === sourceDayNumber) {
            return {
              ...day,
              activities:
                day.activities?.filter((a) => a.activityId !== activityId) || []
            };
          } else if (day.dayNumber === targetDayNumber) {
            return {
              ...day,
              activities: [...(day.activities || []), activityToMove]
            };
          }
          return day;
        });

        return {
          ...prevSelected,
          days: newDays
        };
      });
    },
    []
  );

  // Optimistically remove an activity from trip state; returns snapshot for rollback
  const removeActivityOptimistic = useCallback(
    (activityId: string): ITrip => {
      const snapshot = trip as ITrip;

      setTripState((prevTrip) => ({
        ...prevTrip,
        activities: prevTrip.activities.filter((a) => a.id !== activityId),
        totalActivities: Math.max(0, prevTrip.totalActivities - 1),
        itineraries: prevTrip.itineraries.map((itin) => ({
          ...itin,
          days:
            itin.days?.map((day) => ({
              ...day,
              activities:
                day.activities?.filter((a) => a.activityId !== activityId) ?? []
            })) ?? itin.days
        }))
      }));

      setSelectedItinerary((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          days:
            prev.days?.map((day) => ({
              ...day,
              activities:
                day.activities?.filter((a) => a.activityId !== activityId) ?? []
            })) ?? prev.days
        };
      });

      return snapshot;
    },
    [trip]
  );

  // Wrapper function for setTrip that also updates itinerary
  const setTrip = useCallback((newTrip: ITrip) => {
    setTripState(newTrip);

    // Set itinerary to the first itinerary if available
    if (newTrip.itineraries && newTrip.itineraries.length > 0) {
      setItinerary(newTrip.itineraries[0]);
      setSelectedItinerary(newTrip.itineraries[0]);
    } else {
      setSelectedItinerary(null);
    }
  }, []);

  const value: TripDataContextType = {
    trip,
    setTrip,
    updateTrip,
    refreshTrip,
    setRefreshTrip,
    selectedItinerary,
    setSelectedItinerary,
    itinerary,
    setItinerary,
    addActivity,
    moveActivityBetweenDaysOptimistic,
    removeActivityOptimistic
  };

  return (
    <TripDataContext.Provider value={value}>
      {children}
    </TripDataContext.Provider>
  );
};

export const useTripData = () => {
  const context = useContext(TripDataContext);
  if (context === undefined) {
    throw new Error('useTripData must be used within a TripDataProvider');
  }
  return context;
};
